import moment from "moment";
import Signal from "signals";
import { get, writable } from "svelte/store";
import Analytics from "../Analytics/Analytics";
import API from "../API/API";
import AuthStore from "../Auth/Store";
import LoadState from "../Enums/LoadState";
import GeoHelper from "../Helpers/GeoHelper";
import MapStore from "../Map/Store";
import Bitmap from "../ThirdParty/Bitmap";
import AllFilter from "./ShippingLanes.enums";
import ShippingLanesSetResolution from "./ShippingLanesSetResolution";
import ShippingLanesSetType from "./ShippingLanesSetType";
import ShippingLanesSetTypeData from "./ShippingLanesSetTypeData";
import ShippingLanesStore from "./Store";

class ShippingLanesSet {
	constructor() {
		this._date = moment();
		this._dateStr = ((moment(this._date).startOf("day").subtract(14, "days").toDate().toISOString()).substr(0, 19)) + "+00:00";
		this.type = writable(ShippingLanesSetType.SHIPPING); // type of lanes to pull
		this.resolution = writable(ShippingLanesSetResolution.MEDIUM); // resolution level to query. Should be 0.5, 0.1 or 0.01 see ShippingLanesSetResolution.

		this.currentlySelectedFlag = writable(AllFilter.ALL_FLAGS_READABLE),
		this.currentlySelectedShiptype = writable(AllFilter.ALL_SHIPS_READABLE),
		this.flagFilters = writable([]),
		this.shipTypeFilters = writable([]),

		this.autoResolution = false; // set resolution based on size of bounds.
		this.color = [1.0, 0.0, 0.0, 1.0];

		this.overrideMaxValue = writable(true);
		this.maxValue = writable(500);
		this.actualMaxValue = writable(0);
		this.minValuePercentage = writable(0.01);

		this.resolution.subscribe((resolution) => {
			if (resolution === ShippingLanesSetResolution.HIGH) {
				this.maxValue.set(5);
			}
			else if (resolution === ShippingLanesSetResolution.MEDIUM) {
				this.maxValue.set(100);
			}
			else if (resolution === ShippingLanesSetResolution.LOW) {
				this.maxValue.set(500);
			}
		});

		this.resultOutline = null;
		this.dirty = false;

		this.visible = true;
		this.visibleOutline = true;

		this._extentToData = {};
		this._extentToImageRects = {};
		this._tileStates = {};

		this.error = false;
		this.errorMessage = "";
		this.loadState = LoadState.INIT;

		this.lastUpdated = writable(Date.now());
		this.unsubscribeLastUpdated = this.lastUpdated.subscribe(() => {
			ShippingLanesStore.lastUpdated.set(Date.now());
		});

		this.meta = {
			colorPickVisible: false
		};

		this.setBounds();

		this.events = {
			onProgress: new Signal(),
			onComplete: new Signal()
		};

		this.events.onProgress.add(() => {
			// Check if finished!
			let finished = true;
			const extents = Object.keys(this._tileStates);
			for (let i = 0; i < extents.length; i++) {
				const thisExtent = extents[i];
				if (this._tileStates[thisExtent].loadState !== LoadState.LOADED) {
					finished = false;
					break;
				}
			}
			if (finished) {
				this.loadState = LoadState.LOADED;
				this.events.onComplete.dispatch();
			}
		});
		this.events.onProgress.add(() => {
			this.lastUpdated.set(Date.now());
			ShippingLanesStore.update();
		});
		this.events.onComplete.add(() => {
			this.lastUpdated.set(Date.now());
			ShippingLanesStore.update();
		});
		this.deleted = false;
		ShippingLanesStore.sets.push(this);
		this.lastUpdated.set(Date.now());
	}

	setBounds(topLeft = [-10, -10], bottomRight = [10, 10]) {
		this._tl = topLeft;
		this._br = bottomRight;
		this._extent = [this._tl[0], this._br[0], this._tl[1], this._br[1]]; // [long_min, long_max, lat_min, lat_max ]
		this._rectCoords = this._generateRectCoords(this._extent);
		this.resultOutline = GeoHelper.featureLineString([...this._rectCoords, this._rectCoords[0]]);

		if (this.autoResolution) {
			const lenX = this._extent[1] - this._extent[0];
			const lenY = this._extent[3] - this._extent[2];
			const size = ((lenX * 1000) * (lenY * 1000)) / 1000;
			console.log("bounds size", size);

			const high = 150000;
			const medium = 500000;
			if (size < high) {
				this.resolution.set(ShippingLanesSetResolution.HIGH);
			}
			else if (size < medium) {
				this.resolution.set(ShippingLanesSetResolution.MEDIUM);
			}
			else {
				this.resolution.set(ShippingLanesSetResolution.LOW);
			}
		}
	}

	get resultImages() {
		const keys = Object.keys(this._extentToImageRects);
		const results = [];
		for (let i = 0; i < keys.length; i++) {
			results.push(...this._extentToImageRects[keys[i]]);
		}
		return results;
	}

	_getMaxValue() {
		let maxValue = 0;
		if (get(this.overrideMaxValue)) {
			maxValue = get(this.maxValue);
		}
		else {
			maxValue = get(this.actualMaxValue);
			this.maxValue.set(get(this.actualMaxValue));
		}
		return maxValue;
	}

	_generateRectCoords(extent) {
		return MapStore.helpers.generateRectCoordsFromExtent(extent);
	}

	_generateExtentBitmaps(extent) {
		if (typeof this._extentToImageRects[extent] !== "undefined") {
			this._extentToImageRects[extent] = [];
		}

		const data = this._extentToData[extent];
		const degreesTall = data.meta.extent[3] - data.meta.extent[2];
		const resolution = data.meta.resolution;
		// degreesTall/resolution === height (should)

		const height = data.lanes.length;
		const width = data.lanes[0].length;

		const maxValue = this._getMaxValue();
		this._extentToImageRects[extent] = [];

		let maxPixels = 100;
		if (resolution === ShippingLanesSetResolution.MEDIUM) {
			maxPixels = 10;
		}
		else if (resolution === ShippingLanesSetResolution.LOW) {
			maxPixels = 5;
		}
		const tilesHeight = Math.ceil(height / maxPixels);

		if (typeof data.reversed === "undefined") {
			data.lanes.reverse();
			data.reversed = true;
		}
		for (let ty = 0; ty < tilesHeight; ty++) {
			const thisExtentHeight = maxPixels * resolution;

			// tile-lines end up in the wrong y-order without this inversion.
			 const ity = tilesHeight - 1 - ty;

			const thisExtent = [...extent];
			thisExtent[2] = extent[2] + (thisExtentHeight * ty);
			thisExtent[3] = extent[2] + (thisExtentHeight * (ty + 1));
			if (thisExtent[3] > extent[3]) {
				thisExtent[3] = extent[3];
			}
			const thisPixels = Math.round((thisExtent[3] - thisExtent[2]) / resolution);

			const offsetY = ty * maxPixels;

			let bmp = new Bitmap(width, thisPixels);
			for (let x = 0; x < bmp.width; x++) {
				for (let y = 0; y < bmp.height; y++) {
					const fullnessFactor = (data.lanes[y + offsetY][x] / maxValue);
					bmp.pixel[x][y] = [
						this.color[0],
						this.color[1],
						this.color[2],
						((fullnessFactor > get(this.minValuePercentage)) ? (fullnessFactor * this.color[3]) : 0)
					];
				}
			}
			const url = bmp.dataURL();

			const flippedExtent = [...thisExtent];
			const temp = flippedExtent[2];
			flippedExtent[2] = flippedExtent[3];
			flippedExtent[3] = temp;
			this._extentToImageRects[extent].push(
				GeoHelper.imageRect(url, this._generateRectCoords(flippedExtent))
			);
			bmp = null;
		}
	}

	retryTile(extent) {
		this.loadState = LoadState.LOADING;
		this.error = false;
		this._startTile(extent);
		this.lastUpdated.set(Date.now());
	}

	_startTile(extent) {
		return new Promise((resolve, reject) => {
			const meta = {
				extent: extent,
				date: this._dateStr,
				resolution: get(this.resolution),
				ais_data_type: ShippingLanesSetTypeData[get(this.type)].ais_data_type,
				filters: {
					flag: [AllFilter.ALL_FLAGS],
					shiptype: [AllFilter.ALL_SHIPS]
				}
			};

			if (get(this.type) === ShippingLanesSetType.SHIPPING) {
				const currentlySelectedFlag = get(this.currentlySelectedFlag) === AllFilter.ALL_FLAGS_READABLE ? AllFilter.ALL_FLAGS : get(this.currentlySelectedFlag);
				const currentlySelectedShiptype = get(this.currentlySelectedShiptype) === AllFilter.ALL_SHIPS_READABLE ? AllFilter.ALL_SHIPS : get(this.currentlySelectedShiptype);
				meta.filters = {
					flag: [currentlySelectedFlag],
					shiptype: [currentlySelectedShiptype]
				};
			}

			if (typeof ShippingLanesSetTypeData[get(this.type)].filters !== "undefined") {
				meta.filters = ShippingLanesSetTypeData[get(this.type)].filters;
			}
			if (get(this.type) !== ShippingLanesSetType.SHIPPING) {
				if (get(this.type) === ShippingLanesSetType.FISHING) {
					this._date = moment("2021-12-31");
					this._dateStr = ((moment(this._date).startOf("day").toDate().toISOString()).substr(0, 19)) + "+00:00";
					meta.date = this._dateStr;
				}
				else if (get(this.type) === ShippingLanesSetType.TRAWLING) {
					this._date = moment("2021-03-09");
					this._dateStr = ((moment(this._date).startOf("day").toDate().toISOString()).substr(0, 19)) + "+00:00";
					meta.date = this._dateStr;
				}
			}

			// tile state
			if (!this._tileStates[extent]) {
				this._tileStates[extent] = {};
			}
			this._tileStates[extent].extent = extent;
			this._tileStates[extent].loadState = LoadState.LOADING;
			this._tileStates[extent].error = false;
			this._tileStates[extent].message = "";

			API.post("shipping-lanes", meta)
				.then((response) => {
					if (response.data.errorType) {
						throw new Error(response.data.errorMessage);
					}
					if (response.data.lanes.length === 0) {
						throw new Error("Invalid shipping lane data. No lanes.");
					}

					// width/height should bbe lnglat width/height diff / (divided by) resolution,.
					const height = response.data.lanes.length;
					const width = response.data.lanes[0].length;

					for (let y = 0; y < height; y++) {
						for (let x = 0; x < width; x++) {
							if (response.data.lanes[y][x] > get(this.actualMaxValue)) {
								this.actualMaxValue.set(response.data.lanes[y][x]);
							}
						}
					}

					this._extentToData[extent] = {
						...response.data,
						meta: meta
					};
					this._tileStates[extent].loadState = LoadState.LOADED;
					this._tileStates[extent].error = false;
					this._generateExtentBitmaps(extent);

					this.events.onProgress.dispatch();
					resolve();
				})
				.catch((error) => {
					let message = "Error";
					if (error.title) {
						message = error.title;
					}
					else if (error.message) {
						message = error.message;
					}
					console.error("shipping lanes error", message);
					this._tileStates[extent].loadState = LoadState.INIT;
					this._tileStates[extent].error = true;
					this._tileStates[extent].message = message;
					reject(message);
				});
		});
	}

	start() {
		Analytics.event("query", { category: "Shipping Lanes", username: get(AuthStore.userInfo).username, extent: this._extent, resolution: get(this.resolution) });

		console.log("starting shipping lanes query", this._extent, get(this.resolution), this._dateStr);
		this.loadState = LoadState.LOADING;
		this.error = false;
		this.lastUpdated.set(Date.now());

		this.dirty = true;
		// 0.5 = 500 deg sq
		// 0.1 = 100 deg sq
		// 0.01 = 10 deg sq
		// const eachSzX = Math.min(50, this.resolution * 500);
		// const eachSzY = Math.min(10, this.resolution * 20);

		// squares per data limits
		const eachSzX = Math.min(40, get(this.resolution) * 4000);
		const eachSzY = Math.min(10, get(this.resolution) * 500); // 5 degrees per tile height at high res.
		// const eachSzY = Math.min(10, this.resolution * 1000); // 10 degree per tile height at high res.

		// this.resolution * 800;
		// this.resolution * 3000;
		// 6291556 limit
		// console.log("eachSzX", eachSzX);
		// console.log("eachSzY", eachSzY);

		for (let tx = this._tl[0]; tx < this._br[0]; tx += eachSzX) {
			for (let ty = this._tl[1]; ty < this._br[1]; ty += eachSzY) {
				const tnx = Math.min(tx + eachSzX, this._br[0]);
				const tny = Math.min(ty + eachSzY, this._br[1]);
				const extent = [tx, tnx, ty, tny];
				this._startTile([tx, tnx, ty, tny])
					.then(() => {
						// loop through load states and make sure they're all complete.
						// we do this in onProgress event now.
					})
					.catch((errorMessage) => {
						console.error("error", errorMessage);

						this.error = true;
						this.errorMessage = errorMessage;
						if (!this.anyTilesLoading) {
							this.loadState = LoadState.INIT;
						}

						this.lastUpdated.set(Date.now());
					});
			}
		}
	}

	get errors() {
		const extents = Object.keys(this._tileStates);
		const results = [];
		for (let i = 0; i < extents.length; i++) {
			const thisExtent = extents[i];
			if (this._tileStates[thisExtent].error) {
				results.push(this._tileStates[thisExtent]);
			}
		}
		return results;
	}

	get anyTilesLoading() {
		const extents = Object.keys(this._tileStates);
		for (let i = 0; i < extents.length; i++) {
			const thisExtent = extents[i];
			if (this._tileStates[thisExtent].loadState === LoadState.LOADING) {
				return true;
			}
		}
		return false;
	}

	get percentageComplete() {
		const extents = Object.keys(this._tileStates);
		let countComplete = 0.0;
		for (let i = 0; i < extents.length; i++) {
			const thisExtent = extents[i];
			if (this._tileStates[thisExtent].loadState === LoadState.LOADED) {
				countComplete += 1.0;
			}
		}
		return Math.floor((countComplete / parseFloat(extents.length)) * 100);
	}

	updateImagery() {
		const keys = Object.keys(this._extentToData);
		for (let i = 0; i < keys.length; i++) {
			let extent = keys[i];
			if (typeof extent === "string") {
				extent = extent.split(",");
				for (let j = 0; j < extent.length; j++) {
					extent[j] = parseFloat(extent[j]);
				}
			}
			this._generateExtentBitmaps(extent);
		}
		this.dirty = true;
	}

	destroy() {
		this.deleted = true;

		this.unsubscribeLastUpdated();
		this.events.onProgress = null;
		this.events.onComplete = null;
		this.events = null;

		ShippingLanesStore.update();
	}
}

export default ShippingLanesSet;
